import React, { createContext, useContext, useEffect, useState } from "react";

/**
 * Site content store — single source of truth, persisted in localStorage.
 * All copy is authored in TURKISH (the source language); the site translates
 * to the selected language on screen. The admin panel edits every value here.
 */

const DEFAULTS = {
  // ——— Anasayfa (Hero) ———
  "hero.name": "Duru Ünalan",
  "hero.tag": "işler & sözler",
  "hero.blurb":
    "Bilkent'te Uluslararası İlişkiler son sınıf öğrencisi; aynı zamanda proje lideri, marka kurucusu ve diplomasi çırağı. Brifleri düzenli bir harekete dönüştürüyorum.",
  "hero.words": JSON.stringify([
    "Stratejist",
    "Hikâye anlatıcısı",
    "Koordinatör",
    "Kültürel köprü",
  ]),

  // ——— Manifesto ———
  "manifesto.title": "Çalışma biçimime dair kısa bir manifesto.",
  "manifesto.intro":
    "Toplantı başlamadan önce toplantıda nasıl biri olacağımı bilmek istiyorsan, bunu oku.",
  "manifesto.list": JSON.stringify([
    {
      title: "Brifleri davetiye gibi yazarım.",
      body:
        "Dört yıllık diplomasi eğitimi bana tek bir şey öğretti: en iyi projeler, tablolara giydirilmiş hikâyelerdir. Zaman çizelgesine dokunmadan önce tonla başlarım.",
    },
    {
      title: "Sosyal medya akışlarını haber odası gibi görürüm.",
      body:
        "Burcu Ünlü'nün sosyal medyasını yönetirken, her gönderiyi reklam değil bir haber girişi gibi ele alarak sessiz bir akışı 5,3 kat etkileşime taşıdım.",
    },
    {
      title: "Belirsiz olanı hareket hâlinde tutarım.",
      body:
        "BEST A.Ş. ve Filistin Büyükelçiliği'nde koordinasyonun içinde yaşadım — üç dil, iki takvim, tek ortak sunum. Hareket benim varsayılan hâlim.",
    },
    {
      title: "Bir zanaat pasaportu topluyorum.",
      body:
        "Model BM lojistiği, İK işe alım belgeleri, gümrük evrakları, Adobe Illustrator marka varlıkları. Hepsi aynı oyunun provası: karmaşık şeyleri apaçık hissettirmek.",
    },
  ]),

  // ——— İşler (Deneyim) ———
  "works.title": "Bir okuma odası gibi özgeçmiş.",
  "works.list": JSON.stringify([
    {
      role: "Sosyal Medya Yöneticisi & Asistan",
      org: "Burcu Ünlü (Bağımsız Sanatçı)",
      period: "Ağu 2024 — Oca 2026",
      location: "Ankara / Uzaktan",
      accent: "var(--coral)",
      bullets: [
        "Lansmanlar, koleksiyon çıkışları ve galeri duyuruları için tüm Instagram içerik stratejisini tasarladım.",
        "Sekiz ay içinde tutarlı editoryal paylaşımla etkileşim oranını %533'e kadar artırdım.",
        "Basına hazır portföyler oluşturdum, koleksiyoner görüşmelerini yürüttüm ve haftalık editoryal takvimler ürettim.",
        "Etkinlik günlerini yerinde koordine ettim — akış planından çekim listelerine.",
      ],
      tags: ["İçerik stratejisi", "Analitik", "Marka sesi", "Etkinlik operasyonu"],
    },
    {
      role: "Diplomatik Stajyer",
      org: "Filistin Devleti Büyükelçiliği, Ankara",
      period: "Haz 2025 — Ağu 2025",
      location: "Ankara, TR",
      accent: "var(--cobalt)",
      bullets: [
        "Bölgesel siyasi-ekonomik gelişmeler üzerine 3'ten fazla analitik brifing hazırladım.",
        "Yüksek riskli bir ortamda stratejik karar alma için çok kaynaklı istihbaratı sentezledim.",
        "Yönetim kurulu notlarında ve basında kullanılabilir, protokol öncelikli ve kültürler arası bir raporlama dili geliştirdim.",
      ],
      tags: ["Jeopolitik araştırma", "Stratejik raporlama", "Protokol"],
    },
    {
      role: "Proje Yönetimi Stajyeri",
      org: "BEST A.Ş.",
      period: "Tem 2024 — Ağu 2024",
      location: "Ankara, TR",
      accent: "var(--chartreuse)",
      bullets: [
        "Büyük ölçekli altyapı projeleri için dokümantasyon ve iş akışı takibini sadeleştirdim.",
        "İç raporlama döngüsünün parçalarını üstlendim ve haftalık durum şablonlarını yeniden kurdum.",
        "Kilometre taşlarını ve teslimatları hizalı tutmak için departmanlar arası iletişimi koordine ettim.",
      ],
      tags: ["Jira / Monday", "İş akışı operasyonu", "Raporlama"],
    },
    {
      role: "BM Proje Stajyeri",
      org: "İİBF Kulüpler Birliği — Bilkent",
      period: "Nis 2023 — Kas 2023",
      location: "Bilkent Üniversitesi",
      accent: "var(--coral)",
      bullets: [
        "100'den fazla delegeyi ağırlayan üç günlük bir Model BM konferansını koordine ettim.",
        "Lojistiği, takımlar arası iletişimi ve BM girişimleriyle uyumlu etkinlik planlamasını yürüttüm.",
      ],
      tags: ["Etkinlik operasyonu", "Fonksiyonlar arası", "Gönüllü liderlik"],
    },
    {
      role: "İK Stajyeri",
      org: "Armoni Ltd. Şti.",
      period: "Tem 2023 — Ağu 2023",
      location: "Ankara, TR",
      accent: "var(--cobalt)",
      bullets: [
        "Yeni işe alınanlar için oryantasyon belgelerini ve çalışan kayıtlarını yönettim.",
        "İşe alım döngüsü içinde İK süreç verimliliğini iyileştirdim.",
      ],
      tags: ["İK operasyonu", "Dokümantasyon", "İşe alım"],
    },
    {
      role: "İthalat-İhracat Stajyeri",
      org: "Milano Ltd. Şti.",
      period: "Haz 2022 — Tem 2022",
      location: "Ankara, TR",
      accent: "var(--chartreuse)",
      bullets: [
        "Uluslararası ticaret dokümantasyonu ve lojistik iş akışlarıyla temel düzeyde tanıştım.",
        "Gümrük evraklarını ve uçtan uca ithalat-ihracat süreç takibini destekledim.",
      ],
      tags: ["Ticaret evrakları", "Lojistik"],
    },
  ]),

  // ——— Projeler ———
  "projects.title": "Seçili projeler.",
  "projects.blurb":
    "Kurduğum ve şekillendirdiğim birkaç iş — brifleri düzenli harekete dönüştürmeye dair.",
  "projects.list": JSON.stringify([
    {
      title: "Küresel Sahne",
      blurb:
        "Bir jeopolitik hikâyeyi haftada bir, Z kuşağı bakışıyla çözen iki dilli bir editoryal ve podcast fikri.",
      photo: "",
    },
    {
      title: "Studio Ünlü — marka sistemi",
      blurb:
        "Sanatçı Burcu Ünlü için modüler bir Instagram sistemi: 3 grid deseni, 6 açıklama arketipi, tek bir ses.",
      photo: "",
    },
    {
      title: "MUN·100 Kılavuzu",
      blurb:
        "100'den fazla delegeli bir Model BM için 40 sayfalık dakika dakika akış planı — beklenmedik durumlar için senaryolarla.",
      photo: "",
    },
    {
      title: "Oda 8 — öğrenci günlüğü",
      blurb:
        "Türkiye'de mezun olan bir Uluslararası İlişkiler öğrencisi olmaya dair minik bir baskı + web günlüğü. Röportajlar, çalma listeleri, denemeler.",
      photo: "",
    },
  ]),

  // ——— Yetenekler (Zanaat) ———
  "skills.title": "Zanaat / Sessiz Araçlar",
  "skills.groups": JSON.stringify([
    {
      label: "Ürün & PM",
      color: "var(--cobalt)",
      items: ["Agile / Scrum", "Jira", "Notion", "Asana", "CAPM (Aday)"],
    },
    {
      label: "Analitik",
      color: "var(--coral)",
      items: ["Python (Veri Analizi)", "Google Analytics 4", "Meta / IG Insights"],
    },
    {
      label: "Tasarım & Kreatif",
      color: "var(--chartreuse)",
      items: [
        "Adobe Photoshop",
        "Illustrator",
        "InDesign",
        "Marka Varlığı Üretimi",
      ],
    },
  ]),

  // ——— Diller ———
  "languages.title": "Diller",
  "languages.list": JSON.stringify([
    { name: "Türkçe", level: "Ana dil" },
    { name: "İngilizce", level: "C2 · Profesyonel" },
    { name: "Español", level: "A2 · Başlangıç" },
  ]),

  // ——— Eğitim ———
  "education.title": "Eğitim",
  "education.list": JSON.stringify([
    {
      school: "Bilkent Üniversitesi",
      period: "2021 — 2026",
      degree: "Uluslararası İlişkiler Lisans",
      note:
        "İlgili dersler — İstatistik, Veri Analizi, Stratejik İletişim, Proje Yönetimi.",
      tags: ["CAPM · PMI adayı", "GA4 · devam ediyor"],
    },
  ]),

  // ——— İletişim ———
  "contact.title": "Gel, bir sonraki bölümü birlikte yazalım.",
  "contact.blurb":
    "Stajlara, editoryal işlere, proje operasyonu rollerine ve biraz tuhaf iş birliklerine açığım. TR, EN ya da beceriksiz ES ile e-postalar hoş karşılanır.",
  "contact.email": "durunalan1@gmail.com",
  "contact.phone": "+90 553 129 29 28",
  "contact.linkedin": "https://www.linkedin.com/in/duru-ünalan-5165a4240",

  // ——— Footer ———
  "footer.text":
    "Elle hazırlanmış bir portfolyo · otomasyondan çok özenle kuruldu · '26 baskısı.",
  "footer.colophon": JSON.stringify([
    "Fontlar — Fraunces, Space Grotesk, Space Mono.",
    "Hareket — Framer Motion + Lenis akıcı kaydırma.",
    "Palet — Kemik, Mürekkep, Kobalt, Mercan, Nane.",
  ]),

  // ——— Medya ———
  "photos.hero": "",
  "photos.about": "",
};

const STORAGE_KEY = "duru_site_content_v4";
const ADMIN_TOKEN_KEY = "duru_admin_token";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function loadStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch (e) {
    /* noop */
  }
  return { ...DEFAULTS };
}

async function fetchServerContent() {
  try {
    const res = await fetch(`${API}/site-content`, { cache: "no-store" });
    if (!res.ok) return null;
    const data = await res.json();
    return (data && data.content) || {};
  } catch (e) {
    return null;
  }
}

async function pushServerContent(overrides) {
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  if (!token) return { ok: false, reason: "no-token" };
  try {
    const res = await fetch(`${API}/site-content`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "X-Admin-Token": token,
      },
      body: JSON.stringify({ content: overrides }),
    });
    if (res.status === 401) {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      return { ok: false, reason: "unauthorized" };
    }
    return { ok: res.ok };
  } catch (e) {
    return { ok: false, reason: "network" };
  }
}

const SiteContentContext = createContext({
  store: DEFAULTS,
  get: (k, f) => DEFAULTS[k] || f,
  getList: () => [],
  set: () => {},
  reset: () => {},
});

export function SiteContentProvider({ children }) {
  const [store, setStore] = useState(loadStore);

  // Compute the diff between current store and defaults — we only send overrides.
  const overridesOf = (s) => {
    const out = {};
    for (const k of Object.keys(s)) {
      if (s[k] !== DEFAULTS[k]) out[k] = s[k];
    }
    return out;
  };

  const persist = (next) => {
    setStore(next);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (e) {
      /* noop */
    }
    // Fire-and-forget push to server (only succeeds if logged in as admin).
    pushServerContent(overridesOf(next));
  };

  const getList = (k) => {
    const raw = k in store ? store[k] : DEFAULTS[k];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  };

  const value = {
    store,
    get: (k, fallback) => (k in store ? store[k] : fallback ?? DEFAULTS[k] ?? ""),
    getList,
    setList: (k, arr) => persist({ ...store, [k]: JSON.stringify(arr) }),
    set: (k, v) => persist({ ...store, [k]: v }),
    setMany: (obj) => persist({ ...store, ...obj }),
    reset: () => persist({ ...DEFAULTS }),
    keys: () => Object.keys(store),
  };

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setStore(loadStore());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // On mount, fetch server-side overrides so every visitor sees admin edits.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const remote = await fetchServerContent();
      if (cancelled || !remote) return;
      setStore((prev) => {
        const next = { ...DEFAULTS, ...remote };
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        } catch (e) {
          /* noop */
        }
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <SiteContentContext.Provider value={value}>
      {children}
    </SiteContentContext.Provider>
  );
}

export function useSiteContent() {
  return useContext(SiteContentContext);
}
